import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Filter, Eye, ArrowUpDown } from 'lucide-react';
import { Pagination } from '../../../../../shared/components/Pagination';
import KVSLoader from '../../../../../components/loaders/KVSLoader';
import {
  useGetClassHolisticAnalyticsQuery,
  deriveRecordMetrics,
  getClassDisplayName,
  parseCompetencyValue,
} from '../../../services/holisticApi';

// ── Competency level helpers ────────────────────────────────────────────
type CompetencyLevel = 'Excellent' | 'Good' | 'Average' | 'Below Average';

const toCompetencyLevel = (value: number): CompetencyLevel => {
  if (value >= 85) return 'Excellent';
  if (value >= 70) return 'Good';
  if (value >= 55) return 'Average';
  return 'Below Average';
};

const competencyColor: Record<CompetencyLevel, string> = {
  'Excellent': 'bg-emerald-100 text-emerald-700',
  'Good': 'bg-blue-100 text-blue-700',
  'Average': 'bg-amber-100 text-amber-700',
  'Below Average': 'bg-red-100 text-red-700',
};

type SortKey = 'name' | 'competency' | 'term1' | 'term2' | 'behavior' | 'peer';

const StudentList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── URL params ──────────────────────────────────────────────────────
  const academicYear = Number(searchParams.get('year') || '2025');
  // term1_competency & term2_competency are embedded on every record regardless
  // of which term is queried — so a single call with term=1 is sufficient.
  const term = (Number(searchParams.get('term') || '1') === 2 ? 2 : 1) as 1 | 2;
  const classId = Number(searchParams.get('classId') || '1');
  const schoolId = Number(searchParams.get('schoolId') || '0') || undefined;

  // ── Table state ─────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('competency');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | 'All'>(10);

  // ── Single API call ─────────────────────────────────────────────────
  // The analytics-class endpoint embeds term1_competency and term2_competency
  // JSON strings on every student record, so one request is all we need.
  const { data, isLoading } = useGetClassHolisticAnalyticsQuery(
    { classId, academicYear, term, schoolId },
  );

  // ── Derive student rows from API records ────────────────────────────
  // Competency is calculated from domain scores using deriveRecordMetrics()
  // — same logic as StudentProfile uses via mapAvgStudentAnalyticsToHolisticStudent.
  // Per-term competency bars use parseCompetencyValue on the term JSON strings
  // — same logic as StudentProfile's academic.term1_competency / term2_competency.
  const allStudents = useMemo(() => {
    const records = data?.data || [];
    return records.map((record) => {
      const metrics = deriveRecordMetrics(record, term);
      const competency = +metrics.competency.toFixed(1);

      const first = (record.firstName ?? record.first_name ?? '').trim();
      const middle = (record.middleName ?? record.middle_name ?? '').trim();
      const last = (record.lastName ?? record.last_name ?? '').trim();
      const fullName = [first, middle, last].filter(Boolean).join(' ');

      // Per-term competency from JSON strings (identical to StudentProfile logic)
      const term1Competency = parseCompetencyValue(record.term1_competency);
      const term2Competency = parseCompetencyValue(record.term2_competency);

      const classLabel = record.className || getClassDisplayName(Number(record.class_id));
      const schoolName = record.schoolName || '';

      return {
        id: record.student_id,
        name: fullName || `Student ${record.student_id}`,
        classLabel,
        schoolName,
        competency,
        competencyLevel: toCompetencyLevel(competency),
        behavior: +metrics.behavior.toFixed(1),
        peer: +metrics.peer.toFixed(1),
        term1Competency,
        term2Competency,
        needsSupport: competency < 55,
      };
    });
  }, [data]);

  // ── Filtered + sorted list ──────────────────────────────────────────
  const students = useMemo(() => {
    let list = [...allStudents];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q),
      );
    }
    if (levelFilter) {
      list = list.filter(s => s.competencyLevel === levelFilter);
    }

    list.sort((a, b) => {
      switch (sortKey) {
        case 'name': return sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        case 'competency': return sortAsc ? a.competency - b.competency : b.competency - a.competency;
        case 'term1': return sortAsc ? a.term1Competency - b.term1Competency : b.term1Competency - a.term1Competency;
        case 'term2': return sortAsc ? a.term2Competency - b.term2Competency : b.term2Competency - a.term2Competency;
        case 'behavior': return sortAsc ? a.behavior - b.behavior : b.behavior - a.behavior;
        case 'peer': return sortAsc ? a.peer - b.peer : b.peer - a.peer;
        default: return 0;
      }
    });

    return list;
  }, [allStudents, search, levelFilter, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const effectivePageSize = pageSize === 'All' ? students.length : Number(pageSize);
  const totalPages = Math.max(1, Math.ceil(students.length / (effectivePageSize || 10)));
  const paginated = students.slice((page - 1) * effectivePageSize, page * effectivePageSize);

  const SortHeader: React.FC<{ label: string; k: SortKey }> = ({ label, k }) => (
    <th
      className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 cursor-pointer hover:text-indigo-600 select-none"
      onClick={() => toggleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown size={12} className={sortKey === k ? 'text-indigo-600' : 'text-gray-300'} />
      </span>
    </th>
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <KVSLoader loading size={72} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Student List</h1>
          <p className="text-sm text-gray-500">{students.length} students found</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 w-full"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <select
            value={levelFilter}
            onChange={e => { setLevelFilter(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
          >
            <option value="">All Levels</option>
            <option value="Excellent">Excellent</option>
            <option value="Good">Good</option>
            <option value="Average">Average</option>
            <option value="Below Average">Below Average</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <SortHeader label="Student" k="name" />
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">School Name</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Class</th>
                <SortHeader label="Competency" k="competency" />
                <SortHeader label="Term 1" k="term1" />
                <SortHeader label="Term 2" k="term2" />
                <SortHeader label="Behavior" k="behavior" />
                <SortHeader label="Peer" k="peer" />
                <th className="text-center text-xs font-semibold text-gray-500 uppercase px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((student) => (
                <tr key={student.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-bold">
                        {student.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{student.name}</p>
                        <p className="text-xs text-gray-400">{student.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-700 font-medium">{student.schoolName || '-'}</td>
                  <td className="px-5 py-3 text-sm text-gray-700">{student.classLabel}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${competencyColor[student.competencyLevel]}`}>
                      {student.competencyLevel} ({student.competency}%)
                    </span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className={`text-sm font-semibold ${student.term1Competency >= 70 ? 'text-emerald-600' : student.term1Competency >= 55 ? 'text-amber-600' : 'text-red-600'}`}>
                      {student.term1Competency > 0 ? `${student.term1Competency}%` : '—'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className={`text-sm font-semibold ${student.term2Competency >= 70 ? 'text-emerald-600' : student.term2Competency >= 55 ? 'text-amber-600' : 'text-red-600'}`}>
                      {student.term2Competency > 0 ? `${student.term2Competency}%` : '—'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className={`text-sm font-semibold ${student.behavior >= 75 ? 'text-emerald-600' : student.behavior >= 55 ? 'text-amber-600' : 'text-red-600'}`}>
                      {student.behavior}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className={`text-sm font-semibold ${student.peer >= 75 ? 'text-emerald-600' : student.peer >= 55 ? 'text-amber-600' : 'text-red-600'}`}>
                      {student.peer}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <button
                      onClick={() => navigate(`student/${student.id}/overview`)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition"
                    >
                      <Eye size={13} /> View Profile
                    </button>
                  </td>
                </tr>
              ))}
              {paginated.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-gray-400 text-sm">No students found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Page Size Control */}
        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">Rows per page:</span>
            <select
              value={typeof pageSize === 'number' && [10, 30, 50, 100].includes(pageSize) ? pageSize.toString() : (pageSize === 'All' ? 'All' : 'custom')}
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'All') {
                  setPageSize('All');
                } else if (val === 'custom') {
                  setPageSize(10);
                } else {
                  setPageSize(Number(val));
                }
                setPage(1);
              }}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 font-medium text-gray-700 cursor-pointer"
            >
              <option value="10">10</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="All">All</option>
              <option value="custom">Custom</option>
            </select>

            {pageSize !== 'All' && (
              <input
                type="number"
                min={1}
                max={students.length}
                value={pageSize}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val > 0) {
                    setPageSize(val);
                    setPage(1);
                  }
                }}
                className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 w-16 text-center font-medium text-gray-700"
                placeholder="Num..."
              />
            )}
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalRecords={students.length}
              pageSize={effectivePageSize}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentList;
